import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import {
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Table,
  Button,
  Form,
  Input,
  message,
  Spin,
  Tag,
  Modal,
  Select,
} from 'antd';
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
const { Option } = Select;

interface CarbonCredit {
  id: string;
  creditType: string;
  amount: number;
  generationDate: string;
  verificationStatus: string;
  price?: number;
  marketplaceStatus: string;
}

const CarbonCredits: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState<CarbonCredit[]>([]);
  const [marketplace, setMarketplace] = useState<CarbonCredit[]>([]);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [listModalVisible, setListModalVisible] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchCredits();
      fetchMarketplace();
    }
  }, [user]);

  const fetchCredits = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/carbon-credits`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCredits(response.data.data);
    } catch (error) {
      console.error('Error fetching credits:', error);
      message.error('Failed to load carbon credits');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketplace = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/carbon-credits/marketplace`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setMarketplace(response.data.data);
    } catch (error) {
      console.error('Error fetching marketplace:', error);
    }
  };

  const handleGenerate = async (values: any) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/carbon-credits/generate`,
        {
          source: values.source,
          farmId: values.farmId,
          startDate: values.startDate,
          endDate: values.endDate,
          amount: values.amount,
          conservationMetricsId: values.conservationMetricsId,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      message.success('Carbon credits generated successfully');
      setGenerateModalVisible(false);
      fetchCredits();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to generate credits');
    } finally {
      setLoading(false);
    }
  };

  const handleList = async (values: any) => {
    if (!selectedCredit) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/carbon-credits/${selectedCredit}/list`,
        { price: values.price },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      message.success('Credit listed for sale');
      setListModalVisible(false);
      setSelectedCredit(null);
      fetchCredits();
      fetchMarketplace();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to list credit');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (creditId: string) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/carbon-credits/${creditId}/purchase`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      message.success('Credit purchased successfully');
      fetchMarketplace();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to purchase credit');
    } finally {
      setLoading(false);
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return <Tag color="green" icon={<CheckCircleOutlined />}>Verified</Tag>;
      case 'PENDING':
        return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;
      case 'REJECTED':
        return <Tag color="red" icon={<CloseCircleOutlined />}>Rejected</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const creditsColumns = [
    {
      title: 'Type',
      dataIndex: 'creditType',
      key: 'creditType',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount.toFixed(2)} tons CO₂`,
    },
    {
      title: 'Status',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Marketplace',
      dataIndex: 'marketplaceStatus',
      key: 'marketplaceStatus',
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => price ? `$${price}/ton` : 'Not listed',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CarbonCredit) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedCredit(record.id);
            setListModalVisible(true);
          }}
          disabled={record.verificationStatus !== 'VERIFIED' || record.marketplaceStatus === 'LISTED'}
        >
          List for Sale
        </Button>
      ),
    },
  ];

  const marketplaceColumns = [
    {
      title: 'Type',
      dataIndex: 'creditType',
      key: 'creditType',
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `${amount.toFixed(2)} tons CO₂`,
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `$${price}/ton`,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: CarbonCredit) => (
        <Button
          type="primary"
          icon={<ShoppingCartOutlined />}
          onClick={() => handlePurchase(record.id)}
        >
          Purchase
        </Button>
      ),
    },
  ];

  const totalCredits = credits.reduce((sum, c) => sum + c.amount, 0);
  const verifiedCredits = credits
    .filter(c => c.verificationStatus === 'VERIFIED')
    .reduce((sum, c) => sum + c.amount, 0);
  const listedCredits = credits
    .filter(c => c.marketplaceStatus === 'LISTED')
    .reduce((sum, c) => sum + c.amount, 0);

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>Carbon Credits</Title>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Total Credits"
              value={totalCredits.toFixed(2)}
              suffix="tons CO₂"
              prefix={<DollarOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Verified Credits"
              value={verifiedCredits.toFixed(2)}
              suffix="tons CO₂"
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Listed for Sale"
              value={listedCredits.toFixed(2)}
              suffix="tons CO₂"
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="My Carbon Credits"
        extra={
          <Button
            type="primary"
            onClick={() => setGenerateModalVisible(true)}
          >
            Generate Credits
          </Button>
        }
      >
        <Table
          columns={creditsColumns}
          dataSource={credits}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Card title="Marketplace" style={{ marginTop: 16 }}>
        <Table
          columns={marketplaceColumns}
          dataSource={marketplace}
          rowKey="id"
          loading={loading}
        />
      </Card>

      <Modal
        title="Generate Carbon Credits"
        open={generateModalVisible}
        onCancel={() => setGenerateModalVisible(false)}
        footer={null}
      >
        <Form onFinish={handleGenerate} layout="vertical">
          <Form.Item
            name="source"
            label="Source"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="renewable_energy">Renewable Energy</Option>
              <Option value="conservation">Conservation</Option>
              <Option value="sequestration">Carbon Sequestration</Option>
            </Select>
          </Form.Item>
          <Form.Item name="farmId" label="Farm ID">
            <Input />
          </Form.Item>
          <Form.Item name="startDate" label="Start Date">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="endDate" label="End Date">
            <Input type="date" />
          </Form.Item>
          <Form.Item name="amount" label="Amount (for sequestration)">
            <Input type="number" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Generate
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="List Credit for Sale"
        open={listModalVisible}
        onCancel={() => {
          setListModalVisible(false);
          setSelectedCredit(null);
        }}
        footer={null}
      >
        <Form onFinish={handleList} layout="vertical">
          <Form.Item
            name="price"
            label="Price per ton ($)"
            rules={[{ required: true }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              List for Sale
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CarbonCredits;

